import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { MoreHorizontal, Pencil, Send, Archive, X } from 'lucide-react'
import type { AuditorListItem, AuditorStatus } from '@common/types'
import { AUDITOR_STATUSES } from '@common/types'
import { AuditorStatusBadge } from '@frontend/components/ui/status-badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  SortableTableHead,
} from '@frontend/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@frontend/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@frontend/components/ui/dialog'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'
import { MultiSelect } from '@frontend/components/ui/multi-select'
import { usePermissions } from '@frontend/hooks/usePermissions'
import { useFilters, useSort, type FilterDefinition, type SortColumnDef } from '@frontend/lib/filters'
import { FilterBar } from '@frontend/components/filters/FilterBar'
import { SearchFilter } from '@frontend/components/filters/SearchFilter'
import { AuditorModal } from './AuditorModal'
import { publishAuditorApi, archiveAuditorApi } from '@frontend/lib/api'
import { formatDate, isHttpUrl } from '@frontend/lib/utils'

interface AuditorsTableProps {
  auditors: AuditorListItem[]
}

const AUDITOR_STATUS_LABELS: Record<AuditorStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
}

// Archived auditors are excluded from the list query, so they aren't a filter option.
const STATUS_OPTIONS = AUDITOR_STATUSES.filter((s) => s !== 'archived').map((s) => ({
  value: s,
  label: AUDITOR_STATUS_LABELS[s],
}))

export function AuditorsTable({ auditors }: AuditorsTableProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { can } = usePermissions()

  const [selectedAuditor, setSelectedAuditor] = useState<AuditorListItem | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [archiveTarget, setArchiveTarget] = useState<AuditorListItem | null>(null)

  // Filters
  const filterDefinitions = useMemo<FilterDefinition<AuditorListItem>[]>(
    () => [
      {
        id: 'search',
        label: 'Search',
        type: 'search',
        match: (auditor, value) => {
          const search = value as string
          if (!search) return true
          return auditor.name.toLowerCase().includes(search.toLowerCase())
        },
      },
      {
        id: 'status',
        label: 'Status',
        type: 'multiselect',
        options: STATUS_OPTIONS,
        match: (auditor, value) => {
          const statuses = value as AuditorStatus[]
          if (statuses.length === 0) return true
          return statuses.includes(auditor.status)
        },
      },
    ],
    []
  )

  const { filteredData, filterState, setFilter, clearAllFilters, activeFilterCount } = useFilters(
    auditors,
    filterDefinitions
  )

  const searchValue = (filterState.search as string) || ''
  const statusValue = (filterState.status as AuditorStatus[]) || []

  // Sort
  const sortColumns = useMemo<SortColumnDef<AuditorListItem>[]>(
    () => [
      { id: 'name', accessor: (a) => a.name.toLowerCase() },
      { id: 'status', accessor: (a) => a.status },
      { id: 'reviewCount', accessor: (a) => a.reviewCount },
      { id: 'lastPublishedAt', accessor: (a) => a.lastPublishedAt },
    ],
    []
  )

  const { sortedData, toggleSort, isSorted } = useSort(filteredData, sortColumns, {
    field: 'name',
    direction: 'asc',
  })

  // Mutations
  const publishMutation = useMutation({
    mutationFn: publishAuditorApi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['auditors'] })
      queryClient.invalidateQueries({ queryKey: ['review'] })
      toast.success(`Auditor "${data.auditor.name}" published`)
    },
    onError: () => toast.error('Failed to publish auditor'),
  })

  const archiveMutation = useMutation({
    mutationFn: archiveAuditorApi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['auditors'] })
      queryClient.invalidateQueries({ queryKey: ['review'] })
      setArchiveTarget(null)
      toast.success(`Auditor "${data.auditor.name}" archived`)
    },
    onError: () => {
      setArchiveTarget(null)
      toast.error('Failed to archive auditor')
    },
  })

  return (
    <>
      {/* Filters */}
      <FilterBar className="mb-4">
        <SearchFilter
          value={searchValue}
          onChange={(value) => setFilter('search', value)}
          placeholder="Search auditors..."
        />
        <MultiSelect
          options={STATUS_OPTIONS}
          selected={statusValue}
          onChange={(values) => setFilter('status', values)}
          placeholder="Filter by status..."
          searchPlaceholder="Search statuses..."
          emptyMessage="No statuses found."
        />
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-9 px-2 text-muted-foreground">
            <X className="h-4 w-4 mr-1" />
            Clear filters
          </Button>
        )}
      </FilterBar>

      {/* Results count */}
      <div className="text-sm text-muted-foreground mb-2">
        {sortedData.length} of {auditors.length} auditor{auditors.length !== 1 ? 's' : ''}
        {activeFilterCount > 0 && ' (filtered)'}
      </div>

      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <SortableTableHead sorted={isSorted('name')} onSort={() => toggleSort('name')}>
              Name
            </SortableTableHead>
            <TableHead>Website</TableHead>
            <SortableTableHead sorted={isSorted('status')} onSort={() => toggleSort('status')}>
              Status
            </SortableTableHead>
            <TableHead>Opinion</TableHead>
            <SortableTableHead sorted={isSorted('reviewCount')} onSort={() => toggleSort('reviewCount')}>
              Reviews
            </SortableTableHead>
            <SortableTableHead sorted={isSorted('lastPublishedAt')} onSort={() => toggleSort('lastPublishedAt')}>
              Last Published
            </SortableTableHead>
            <TableHead className="w-[50px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                {auditors.length === 0 ? 'No auditors yet.' : 'No auditors match your filters.'}
              </TableCell>
            </TableRow>
          ) : (
            sortedData.map((auditor) => (
              <TableRow
                key={auditor.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => navigate(`/auditors/${auditor.id}`)}
              >
                <TableCell className="font-medium">{auditor.name}</TableCell>
                <TableCell>
                  {auditor.website && isHttpUrl(auditor.website) ? (
                    <a
                      href={auditor.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {new URL(auditor.website).hostname}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <AuditorStatusBadge status={auditor.status} />
                </TableCell>
                <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                  {auditor.opinion || '—'}
                </TableCell>
                <TableCell>{auditor.reviewCount}</TableCell>
                <TableCell>{formatDate(auditor.lastPublishedAt)}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      {can('auditors.edit') && (
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedAuditor(auditor)
                            setEditOpen(true)
                          }}
                        >
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                      )}
                      {can('auditors.publish') && auditor.status === 'draft' && auditor.opinion && (
                        <DropdownMenuItem
                          onClick={() => publishMutation.mutate(auditor.id)}
                          disabled={publishMutation.isPending}
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Publish
                        </DropdownMenuItem>
                      )}
                      {can('auditors.archive') && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={() => setArchiveTarget(auditor)}>
                            <Archive className="h-4 w-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Edit Modal */}
      {selectedAuditor && (
        <AuditorModal mode="edit" open={editOpen} onOpenChange={setEditOpen} auditor={selectedAuditor} />
      )}

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={!!archiveTarget}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Auditor</DialogTitle>
            <DialogDescription>
              Are you sure you want to archive &ldquo;{archiveTarget?.name}&rdquo;? This will hide the auditor from the
              list. Existing review links will be preserved.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => archiveTarget && archiveMutation.mutate(archiveTarget.id)}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? <Spinner size="sm" className="mr-2" /> : null}
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
